import { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { ViewContext } from 'components/lib';
import { consumeGridVolume, getGridVolumeUsage, type GridVolumeUsage } from 'api/gridApi';
import { getApiErrorMessage } from 'api/apiClient';
import { formatCubicMeters } from 'utils/formatCubicMeters';

type VolumeUsageDialogProps = {
  open: boolean;
  gridId: string | null;
  initialUsage?: GridVolumeUsage | null;
  onClose: () => void;
  onConsumed?: (usage: GridVolumeUsage) => void | Promise<void>;
};

export default function VolumeUsageDialog({
  open,
  gridId,
  initialUsage = null,
  onClose,
  onConsumed,
}: VolumeUsageDialogProps) {
  const location = useLocation();
  const viewContext = useContext(ViewContext);
  const [usage, setUsage] = useState(initialUsage);
  const [loading, setLoading] = useState(false);
  const [consuming, setConsuming] = useState(false);

  useEffect(() => {
    if (!open || !gridId) return;
    setUsage(initialUsage);
    if (initialUsage) return;
    setLoading(true);
    getGridVolumeUsage(gridId)
      .then(setUsage)
      .catch((error) => {
        viewContext.notification.show(
          getApiErrorMessage(error, 'Unable to load volume usage right now.'),
          'error',
          true,
          'toast'
        );
        onClose();
      })
      .finally(() => setLoading(false));
  }, [gridId, open]);

  const insufficientCredits = Boolean(usage && usage.volumeCredits < usage.volumeDifference);
  const buyVolumeUrl = `/account/buy-volume?returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`;

  const consume = async () => {
    if (!gridId || consuming || insufficientCredits) return;
    setConsuming(true);
    try {
      const nextUsage = await consumeGridVolume(gridId);
      setUsage(nextUsage);
      viewContext.notification.show('Volume credits consumed successfully', 'success', true, 'toast');
      await onConsumed?.(nextUsage);
      onClose();
    } catch (error) {
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to consume volume credits right now'),
        'error',
        true,
        'toast'
      );
    } finally {
      setConsuming(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !consuming && onClose()}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { sx: { backgroundColor: '#ececec', maxWidth: 430 } } }}
    >
      <DialogTitle sx={{ fontWeight: 700, backgroundColor: '#ececec' }}>Confirm volume usage</DialogTitle>
      <DialogContent>
        {loading && !usage ? (
          <div style={{ display: 'grid', minHeight: 80, placeItems: 'center' }}>
            <CircularProgress size={24} />
          </div>
        ) : usage ? (
          <div style={{ display: 'grid', gap: 12, paddingTop: 8 }}>
            {[
              ['Available volume credits:', usage.volumeCredits],
              ...(usage.paidVolume > 0 ? [['Volume already used in this project:', usage.paidVolume]] : []),
              ['Credits to consume:', usage.volumeDifference],
            ].map(([label, value]) => (
              <div key={String(label)} style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
                <Typography sx={{ color: '#4b5563' }}>{label}</Typography>
                <Typography sx={{ fontWeight: 700 }}>{formatCubicMeters(Number(value))} m³</Typography>
              </div>
            ))}
            {insufficientCredits ? (
              <Typography sx={{ color: '#b91c1c', paddingTop: 1 }}>
                You do not have enough volume credits. Please{' '}
                <Link style={{ color: 'inherit', cursor: 'pointer', textDecoration: 'underline' }} to={buyVolumeUrl}>
                  buy more volume
                </Link>{' '}
                before continuing.
              </Typography>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px 20px', backgroundColor: '#ececec' }}>
        <Button variant="contained" className="modal-no-btn" onClick={onClose} disabled={consuming}>
          CANCEL
        </Button>
        <Button
          variant="contained"
          className="modal-yes-btn"
          onClick={consume}
          disabled={loading || consuming || !usage || insufficientCredits}
        >
          {consuming ? <CircularProgress size={18} thickness={5} color="inherit" /> : 'CONFIRM'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
